import { useState } from "react";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatMoney } from "../../lib/api";
import { MoneyAmount } from "../../components/MoneyAmount";
import { MoneyTotal } from "../../components/MoneyTotal";
import { DEBT_ICONS, pickIcon } from "../../lib/typeIcons";
import { useDebts, useDebtsMutations, useProfile, useFxRates, useFxConvert, useCashAccounts } from "../../hooks/queries";
import { useEntitlements } from "../../hooks/useEntitlements";
import { LockedSection } from "../../components/LockedSection";
import { DatePicker } from "../../components/DatePicker";
import { AccountPickerField } from "../../components/AccountPickerField";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { HighlightCard } from "../../components/HighlightCard";
import { toast } from "sonner";
import { reportValidationError } from "../../lib/validationTelemetry";
import { Plus, CreditCard, Pencil, Trash2, Banknote } from "lucide-react";

const KINDS = [
  { v: "credit_card", labelEn: "Credit card", labelEs: "Tarjeta de crédito" },
  { v: "personal_loan", labelEn: "Personal loan", labelEs: "Libre inversión" },
  { v: "mortgage", labelEn: "Mortgage", labelEs: "Hipoteca" },
  { v: "auto_loan", labelEn: "Auto loan", labelEs: "Crédito vehículo" },
  { v: "revolving_credit", labelEn: "Revolving credit", labelEs: "Crédito rotativo" },
  { v: "other", labelEn: "Other", labelEs: "Otro" },
];

// Revolving credit lines (tarjeta + rotativo) don't have a fixed payoff date
// nor an "original disbursed amount". Hide those fields so the form matches
// the user's mental model.
const REVOLVING_KINDS = new Set(["credit_card", "revolving_credit"]);

export default function Debts() {
  const { t, lang } = useLang();
  const { data: items = [] } = useDebts();
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const { create, update, remove: removeMut, pay } = useDebtsMutations();
  const { data: fxInfo } = useFxRates();
  const fxList = Object.keys(fxInfo?.rates || {}).sort();
  const convert = useFxConvert();
  const { data: cashAccounts = [] } = useCashAccounts();
  // iter-170af — Amortization is a catalog feature. We gate the
  // per-debt projection block AND insert a single LockedSection
  // upsell card right above the debt list when the user has debts
  // but lacks the feature, so the value prop is explicit.
  const { has: hasFeature, isLoading: entLoading } = useEntitlements();
  const amortLocked = !entLoading && !hasFeature("amortization");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [form, setForm] = useState({
    name: "", kind: "credit_card", principal: "", balance: "",
    interest_rate: "", monthly_payment: "", next_due_date: "", end_date: "", currency: "", notes: "",
    credit_limit: "", billing_day: "", cutoff_day: "", account_id: "",
  });
  // iter-169zze · field-level error map for inline highlighting.
  const [fieldErrors, setFieldErrors] = useState({});

  const openNew = () => {
    setEditing(null);
    setFieldErrors({});
    setForm({ name: "", kind: "credit_card", principal: "", balance: "", interest_rate: "", monthly_payment: "", next_due_date: "", end_date: "", currency: "", notes: "", credit_limit: "", billing_day: "", cutoff_day: "", account_id: "" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setFieldErrors({});
    setForm({
      name: it.name, kind: it.kind,
      principal: String(it.principal || 0),
      balance: String(it.balance || 0),
      interest_rate: String(it.interest_rate || 0),
      monthly_payment: String(it.monthly_payment || 0),
      next_due_date: it.next_due_date ? it.next_due_date.slice(0, 10) : "",
      end_date: it.end_date ? it.end_date.slice(0, 10) : "",
      currency: it.currency || "",
      notes: it.notes || "",
      credit_limit: it.credit_limit != null ? String(it.credit_limit) : "",
      billing_day: it.billing_day != null ? String(it.billing_day) : "",
      cutoff_day: it.cutoff_day != null ? String(it.cutoff_day) : "",
      account_id: it.account_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const isEs = lang === "es";
    const fe = {};
    if (!form.name?.trim()) fe.name = isEs ? "El nombre es obligatorio." : "Name is required.";
    if (!form.balance || Number(form.balance) < 0) {
      fe.balance = isEs ? "Indica el saldo actual." : "Enter the current balance.";
    }
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      reportValidationError("debt-form", fe);
      toast.error(isEs ? "Faltan campos obligatorios." : "Missing required fields.");
      return;
    }
    setFieldErrors({});
    const isRevolving = REVOLVING_KINDS.has(form.kind);
    const dayOrNull = (v) => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n >= 1 && n <= 31 ? n : null;
    };
    // For revolving, the next due date is derived from billing_day (the
    // statement day of the month). Use the next future occurrence so the
    // dashboard's upcoming-payments aggregator picks it up correctly.
    const nextDueFromBillingDay = (day) => {
      if (!day) return null;
      const today = new Date();
      let target = new Date(today.getFullYear(), today.getMonth(), day);
      if (target < today) target = new Date(today.getFullYear(), today.getMonth() + 1, day);
      // ISO YYYY-MM-DD (no time portion required by the backend)
      return target.toISOString().slice(0, 10);
    };
    const revolvingBillingDay = isRevolving ? dayOrNull(form.billing_day) : null;
    const payload = {
      name: form.name, kind: form.kind,
      // Revolving credit lines: principal === balance (no fixed disbursed amount)
      // and there is no end_date. Term loans keep both.
      principal: isRevolving ? Number(form.balance) : Number(form.principal || form.balance),
      balance: Number(form.balance),
      interest_rate: Number(form.interest_rate || 0),
      monthly_payment: Number(form.monthly_payment || 0),
      next_due_date: isRevolving
        ? (nextDueFromBillingDay(revolvingBillingDay) || form.next_due_date || null)
        : (form.next_due_date || null),
      end_date: isRevolving ? null : (form.end_date || null),
      currency: form.currency || currency,
      notes: form.notes || "",
      // Revolving-only metadata (server validates as optional; backend
      // ignores when not applicable). `minimum_payment` is intentionally
      // omitted — we reuse `monthly_payment` ("cuota mínima mensual")
      // as the single source of truth for revolving lines.
      credit_limit: isRevolving && form.credit_limit !== "" ? Number(form.credit_limit) : null,
      billing_day: isRevolving ? revolvingBillingDay : null,
      cutoff_day: isRevolving ? dayOrNull(form.cutoff_day) : null,
      // Funding account: propagated to the auto-generated installment
      // payment so /Pagos shows where the cuota is debited from. Required
      // by the new "Cuenta de origen" form field for any debt that
      // generates installments (revolving with billing_day OR term loan
      // with monthly_payment). Empty ⇒ primary account fallback (legacy
      // behaviour, kept for backwards compat with existing debts).
      account_id: form.account_id || null,
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, payload });
      else await create.mutateAsync(payload);
      toast.success(tx(lang, "Saved", "Guardado", "Guardado"));
      setOpen(false);
    } catch (e) { console.error("save debt failed", e); /* onError toast already fired */ }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    await removeMut.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
    toast.success(tx(lang, "Deleted", "Eliminado", "Eliminado"));
  };

  const submitPay = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) return toast.error(tx(lang, "Invalid amount", "Monto inválido", "Monto inválido"));
    try {
      await pay.mutateAsync({ id: payTarget.id, amount: amt });
      toast.success(tx(lang, "Payment recorded", "Pago registrado", "Pago registrado"));
      setPayTarget(null); setPayAmount("");
    } catch (e) { console.error("debt payment failed", e); /* onError toast already fired */ }
  };

  const totalsItems = items.map((d) => ({ amount: d.balance || 0, currency: d.currency || currency }));
  const monthlyItems = items.map((d) => ({ amount: d.monthly_payment || 0, currency: d.currency || currency }));

  const kindLabel = (v) => (KINDS.find((k) => k.v === v) || {})[tx(lang, "labelEn", "labelEs", "labelEs")] || v;

  // Totales para el subtítulo del header — convierte cada deuda a la moneda
  // base del usuario para un único número de cabecera (los desgloses por
  // moneda viven en las cards de KPI más abajo).
  const totalOwedBase = items.reduce(
    (s, d) => s + convert(Number(d.balance || 0), d.currency || currency, currency),
    0,
  );

  return (
    <Layout>
      <PageHeader
        eyebrow={tx(lang, "Outstanding commitments", "Compromisos a saldar", "Compromisos a saldar")}
        title={tx(lang, "Debts", "Deudas", "Deudas")}
        subtitle={items.length === 0
          ? (tx(lang, "You're completely debt-free.", "Estás 100% libre de deudas.", "Estás 100% libre de deudas."))
          : (tx(lang, `${items.length} ${items.length === 1 ? "debt" : "debts"} · ${formatMoney(totalOwedBase, currency)} outstanding`, `${items.length} ${items.length === 1 ? "deuda" : "deudas"} · ${formatMoney(totalOwedBase, currency)} pendientes`, `${items.length} ${items.length === 1 ? "deuda" : "deudas"} · ${formatMoney(totalOwedBase, currency)} pendientes`))}
        testid="debts-header"
        actions={
          <Button data-testid="add-debt-btn" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl gap-2">
            <Plus className="h-4 w-4" /> {tx(lang, "New debt", "Nueva deuda", "Nueva deuda")}
          </Button>
        }
      />
      {/* Legacy title kept hidden for any tooling/test that still queries by
          data-testid="debts-title". The visible title now lives inside
          PageHeader (data-testid="page-header-title"). */}
      <h1 className="sr-only" data-testid="debts-title">
        {tx(lang, "Debts", "Deudas", "Deudas")}
      </h1>

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <Card className="p-4 rounded-2xl border-slate-200">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{tx(lang, "Total balance", "Saldo total", "Saldo total")}</div>
          <MoneyTotal
            items={totalsItems}
            currency={currency}
            tone="expense"
            size="lg"
            testid="debts-total-balance"
            className="mt-1"
          />
        </Card>
        <Card className="p-4 rounded-2xl border-slate-200">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{tx(lang, "Monthly payment", "Cuota mensual", "Cuota mensual")}</div>
          <MoneyTotal
            items={monthlyItems}
            currency={currency}
            size="lg"
            testid="debts-total-monthly"
            className="mt-1"
          />
        </Card>
      </div>

      <div className="grid gap-3" data-testid="debts-list">
        {items.length === 0 && (
          <Card className="p-8 text-center text-slate-500 rounded-2xl">
            {tx(lang, "No debts yet.", "No tienes deudas registradas.", "No tienes deudas registradas.")}
          </Card>
        )}
        {/* iter-170af — Single upsell row at the top of the list when
            the user has debts but lacks the amortization feature. The
            per-debt block is hidden (backend returns null); the
            LockedSection sells the projection (months to payoff,
            total interest, payoff date). */}
        {items.length > 0 && amortLocked && (
          <LockedSection
            featureKey="amortization"
            titleEs="Amortización y proyección de deudas"
            titleEn="Debt amortization & projection"
            titlePt="Amortização e projeção de dívidas"
            bullets={[
              { es: "Meses al pago total de cada deuda",
                en: "Months to payoff per debt",
                pt: "Meses para quitação por dívida" },
              { es: "Interés total proyectado a la tasa actual",
                en: "Projected total interest at current rate",
                pt: "Juros totais projetados à taxa atual" },
              { es: "Fecha estimada de cierre por deuda",
                en: "Estimated payoff date per debt",
                pt: "Data estimada de quitação por dívida" },
              { es: "Alerta cuando la cuota no cubre el interés",
                en: "Alert when the payment doesn't cover interest",
                pt: "Alerta quando a parcela não cobre os juros" },
            ]}
          />
        )}
        {items.map((d) => {
          // Progress bar answers "Cuánto debo?" (not "cuánto he pagado?"):
          //   • Revolving / TC: balance / credit_limit (0% = libre, 100% = cupo agotado)
          //   • Term loans:     balance / principal    (0% = pagado, 100% = recién prestado)
          // When neither denominator is set, default to 0 (no bar shown).
          const isRevolving = REVOLVING_KINDS.has(d.kind);
          const limit = isRevolving ? Number(d.credit_limit || 0) : Number(d.principal || 0);
          const owed = Number(d.balance || 0);
          const owedPct = limit > 0 ? Math.min(100, (owed / limit) * 100) : 0;
          return (
            <HighlightCard key={d.id} kind="debt" id={d.id} data-testid={`debt-${d.id}`} className="p-4 sm:p-5 rounded-2xl border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                    {(() => { const KindIcon = pickIcon(DEBT_ICONS, d.kind); return <KindIcon className="h-5 w-5" />; })()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{d.name}</div>
                    <div className="text-xs text-slate-500">{kindLabel(d.kind)} • {Number(d.interest_rate || 0)}% APR</div>
                    {REVOLVING_KINDS.has(d.kind) && (d.billing_day || d.cutoff_day) && (
                      <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums" data-testid={`debt-cycle-${d.id}`}>
                        {d.billing_day ? (tx(lang, `Due day ${d.billing_day}`, `Vence día ${d.billing_day}`, `Vence día ${d.billing_day}`)) : ""}
                        {d.billing_day && d.cutoff_day ? " · " : ""}
                        {d.cutoff_day ? (tx(lang, `Closes day ${d.cutoff_day}`, `Corte día ${d.cutoff_day}`, `Corte día ${d.cutoff_day}`)) : ""}
                      </div>
                    )}
                    {d.notes && (
                      <div
                        data-testid={`debt-notes-${d.id}`}
                        className="text-[11px] text-slate-500 mt-0.5 italic truncate max-w-xs"
                        title={d.notes}
                      >
                        “{d.notes}”
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                      {REVOLVING_KINDS.has(d.kind)
                        ? (tx(lang, "Used", "Consumido", "Consumido"))
                        : (tx(lang, "Balance", "Saldo", "Saldo"))}
                    </div>
                    <MoneyAmount
                      amount={convert(Number(d.balance || 0), d.currency || currency, currency)}
                      currency={currency}
                      original_currency={d.currency && d.currency !== currency ? d.currency : null}
                      original_amount={d.currency && d.currency !== currency ? d.balance : null}
                      fx_rate={d.fx_rate}
                      tone="expense"
                      size="md"
                      testid={`debt-balance-${d.id}`}
                    />
                  </div>
                  <Button size="sm" variant="outline" data-testid={`pay-debt-${d.id}`} onClick={() => { setPayTarget(d); setPayAmount(String(d.monthly_payment || "")); }} className="gap-1">
                    <Banknote className="h-4 w-4" /> {tx(lang, "Pay", "Pagar", "Pagar")}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-debt-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(d)} data-testid={`delete-debt-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              </div>
              {limit > 0 && (() => {
                const debtCur = d.currency || currency;
                const owedDisplay = convert(owed, debtCur, currency);
                const limitDisplay = convert(limit, debtCur, currency);
                const isFx = debtCur !== currency;
                return (
                <div className="mt-3" data-testid={`debt-owed-progress-${d.id}`}>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span className="flex items-center gap-1 flex-wrap">
                      <span>
                        {tx(lang, "Owed", "Debo", "Debo")}:{" "}
                        <strong className="text-red-600 tabular-nums">
                          {isFx && <span className="text-slate-400 font-medium">≈ </span>}
                          {formatMoney(owedDisplay, currency)}
                        </strong>
                      </span>
                      <span className="text-slate-400">
                        / {isFx && "≈ "}{formatMoney(limitDisplay, currency)}
                      </span>
                      {isFx && (
                        <span className="text-[10px] text-slate-400 tabular-nums" data-testid={`debt-owed-orig-${d.id}`}>
                          ({formatMoney(owed, debtCur)} / {formatMoney(limit, debtCur)} {debtCur})
                        </span>
                      )}
                    </span>
                    <span className={`tabular-nums font-semibold ${owedPct >= 80 ? "text-red-600" : owedPct >= 50 ? "text-amber-600" : "text-emerald-600"}`}>
                      {owedPct.toFixed(1)}%
                    </span>
                  </div>
                  {/* Custom bar — Progress uses primary fill which is misleading
                      for debt (high = bad). Inline divs let us tint red as the
                      bar grows so the visual encodes "más rojo = más debo". */}
                  <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      data-testid={`debt-owed-bar-${d.id}`}
                      style={{ width: `${owedPct}%` }}
                      className={`h-full transition-all ${owedPct >= 80 ? "bg-red-500" : owedPct >= 50 ? "bg-amber-500" : "bg-emerald-500"}`}
                    />
                  </div>
                </div>
                );
              })()}
              {d.amortization && (
                <div
                  data-testid={`debt-amort-${d.id}`}
                  className="mt-3 text-[11px] grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 rounded-lg bg-slate-50 border border-slate-100"
                >
                  {d.amortization.will_amortize ? (
                    <>
                      <div>
                        <div className="uppercase tracking-wider text-slate-500">{tx(lang, "Months to payoff", "Meses al pago total", "Meses al pago total")}</div>
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">{d.amortization.months_to_payoff}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-slate-500">{tx(lang, "Total interest", "Interés total", "Interés total")}</div>
                        <div className="text-sm font-semibold text-amber-700 tabular-nums">
                          {formatMoney(d.amortization.total_interest, d.currency || currency)}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wider text-slate-500">{tx(lang, "Payoff date", "Fecha estimada", "Fecha estimada")}</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {d.amortization.payoff_date_iso ? new Date(d.amortization.payoff_date_iso).toLocaleDateString() : "—"}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="sm:col-span-3 text-amber-700">
                      ⚠️ {tx(lang, `Monthly payment doesn't cover interest. Min to amortize: ${formatMoney(d.amortization.min_payment_to_amortize, d.currency || currency)}`, `La cuota mensual no cubre el interés. Mínimo para amortizar: ${formatMoney(d.amortization.min_payment_to_amortize, d.currency || currency)}`, `La cuota mensual no cubre el interés. Mínimo para amortizar: ${formatMoney(d.amortization.min_payment_to_amortize, d.currency || currency)}`)}
                    </div>
                  )}
                </div>
              )}
            </HighlightCard>
          );
        })}
      </div>

      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        testid="debt-dialog"
        size="lg"
        title={editing ? (tx(lang, "Edit debt", "Editar deuda", "Editar deuda")) : (tx(lang, "New debt", "Nueva deuda", "Nueva deuda"))}
        description={tx(lang, "Capture your debt details", "Captura los datos de tu deuda", "Captura los datos de tu deuda")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="debt-form-save" onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{t("save")}</Button>
          </div>
        }
      >
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="debt-form-name" className="flex items-center gap-1">
                  {tx(lang, "Name", "Nombre", "Nombre")}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="debt-form-name"
                  data-testid="debt-form-name"
                  value={form.name}
                  onChange={(e) => { setForm({ ...form, name: e.target.value }); if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: undefined }); }}
                  placeholder="TC Mastercard"
                  aria-invalid={fieldErrors.name ? "true" : undefined}
                  className={fieldErrors.name ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
                />
                {fieldErrors.name && (
                  <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="debt-form-name-error">{fieldErrors.name}</p>
                )}
              </div>
              <div className="col-span-2">
                <Label>{tx(lang, "Type", "Tipo", "Tipo")}</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="debt-form-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KINDS.map((k) => {
                      const KIcon = pickIcon(DEBT_ICONS, k.v);
                      return (
                        <SelectItem key={k.v} value={k.v}>
                          <span className="inline-flex items-center gap-2">
                            <KIcon className="h-4 w-4 text-slate-500" />
                            {tx(lang, k.labelEn, k.labelEs, k.labelPt || k.labelEs)}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1" data-testid="debt-form-kind-hint">
                  {REVOLVING_KINDS.has(form.kind)
                    ? (tx(lang, "Revolving credit: limit, current used amount, minimum monthly payment and billing/cutoff days.", "Crédito rotativo: cupo, consumo actual, cuota mínima mensual y fechas de facturación/corte.", "Crédito rotativo: cupo, consumo actual, cuota mínima mensual y fechas de facturación/corte."))
                    : (tx(lang, "Term loan: fixed monthly payment and a payoff end date.", "Crédito a plazo fijo: cuota constante y fecha de fin del préstamo.", "Crédito a plazo fijo: cuota constante y fecha de fin del préstamo."))}
                </p>
              </div>
              {!REVOLVING_KINDS.has(form.kind) && (
                <div data-testid="debt-form-principal-wrap">
                  <Label>{tx(lang, "Original amount", "Monto original", "Monto original")}</Label>
                  <Input data-testid="debt-form-principal" type="number" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
                </div>
              )}
              {REVOLVING_KINDS.has(form.kind) && (
                <div className="col-span-2" data-testid="debt-form-credit-limit-wrap">
                  <Label>{tx(lang, "Credit limit", "Cupo / monto máximo", "Cupo / monto máximo")}</Label>
                  <Input data-testid="debt-form-credit-limit" type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder={tx(lang, "Limit provided by the card", "Tope que provee la TC", "Tope que provee la TC")} />
                </div>
              )}
              <div className={REVOLVING_KINDS.has(form.kind) ? "col-span-2" : ""}>
                <Label htmlFor="debt-form-balance" className="flex items-center gap-1">
                  {REVOLVING_KINDS.has(form.kind)
                    ? (tx(lang, "Used", "Consumido", "Consumido"))
                    : (tx(lang, "Current balance", "Saldo actual", "Saldo actual"))}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="debt-form-balance"
                  data-testid="debt-form-balance"
                  type="number"
                  value={form.balance}
                  onChange={(e) => { setForm({ ...form, balance: e.target.value }); if (fieldErrors.balance) setFieldErrors({ ...fieldErrors, balance: undefined }); }}
                  placeholder={REVOLVING_KINDS.has(form.kind)
                    ? (tx(lang, "Amount already spent on the card", "Lo que ya gastaste con la TC", "Lo que ya gastaste con la TC"))
                    : undefined}
                  aria-invalid={fieldErrors.balance ? "true" : undefined}
                  className={fieldErrors.balance ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
                />
                {fieldErrors.balance && (
                  <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="debt-form-balance-error">{fieldErrors.balance}</p>
                )}
                {REVOLVING_KINDS.has(form.kind) && form.credit_limit && Number(form.credit_limit) > 0 && form.balance && (
                  <p className="text-[10px] text-slate-500 mt-0.5 tabular-nums" data-testid="debt-form-util-hint">
                    {tx(lang, `Used: ${Math.round((Number(form.balance) / Number(form.credit_limit)) * 100)}% · Available: ${formatMoney(Math.max(0, Number(form.credit_limit) - Number(form.balance)), form.currency || currency)}`, `Uso: ${Math.round((Number(form.balance) / Number(form.credit_limit)) * 100)}% · Disponible: ${formatMoney(Math.max(0, Number(form.credit_limit) - Number(form.balance)), form.currency || currency)}`, `Uso: ${Math.round((Number(form.balance) / Number(form.credit_limit)) * 100)}% · Disponible: ${formatMoney(Math.max(0, Number(form.credit_limit) - Number(form.balance)), form.currency || currency)}`)}
                  </p>
                )}
              </div>
              <div>
                <Label>{tx(lang, "Interest rate (annual %)", "Tasa interés (% anual)", "Tasa interés (% anual)")}</Label>
                <Input data-testid="debt-form-rate" type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} />
              </div>
              <div>
                <Label>
                  {REVOLVING_KINDS.has(form.kind)
                    ? (tx(lang, "Minimum monthly payment", "Cuota mínima mensual", "Cuota mínima mensual"))
                    : (tx(lang, "Monthly payment", "Cuota mensual", "Cuota mensual"))}
                </Label>
                <Input data-testid="debt-form-monthly" type="number" value={form.monthly_payment} onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })} />
              </div>
              {REVOLVING_KINDS.has(form.kind) && (
                <>
                  <div data-testid="debt-form-billing-day-wrap">
                    <Label>{tx(lang, "Statement/billing day", "Día de facturación", "Día de facturación")}</Label>
                    <Input data-testid="debt-form-billing-day" type="number" min="1" max="31" value={form.billing_day} onChange={(e) => setForm({ ...form, billing_day: e.target.value })} placeholder="1–31" />
                    <p className="text-[10px] text-slate-500 mt-0.5">{tx(lang, "Day of the month payment is due — the next due date is computed automatically.", "Día del mes en que vence el pago — la fecha de vencimiento se calcula automáticamente.", "Día del mes en que vence el pago — la fecha de vencimiento se calcula automáticamente.")}</p>
                  </div>
                  <div data-testid="debt-form-cutoff-day-wrap">
                    <Label>{tx(lang, "Statement closing day", "Día de corte", "Día de corte")}</Label>
                    <Input data-testid="debt-form-cutoff-day" type="number" min="1" max="31" value={form.cutoff_day} onChange={(e) => setForm({ ...form, cutoff_day: e.target.value })} placeholder="1–31" />
                    <p className="text-[10px] text-slate-500 mt-0.5">{tx(lang, "Charges up to this day fall on the current statement; later ones roll to the next cycle.", "Movimientos hasta este día caen en el extracto actual; los siguientes pasan al próximo ciclo.", "Movimientos hasta este día caen en el extracto actual; los siguientes pasan al próximo ciclo.")}</p>
                  </div>
                </>
              )}
              {!REVOLVING_KINDS.has(form.kind) && (
                <div data-testid="debt-form-next-wrap">
                  <Label>{tx(lang, "Next due date", "Próximo vencimiento", "Próximo vencimiento")}</Label>
                  <DatePicker
                    testid="debt-form-next"
                    value={form.next_due_date}
                    onChange={(v) => setForm({ ...form, next_due_date: v })}
                  />
                </div>
              )}
              {!REVOLVING_KINDS.has(form.kind) && (
                <div data-testid="debt-form-end-wrap">
                  <Label>{tx(lang, "End date", "Fecha fin", "Fecha fin")}</Label>
                  <DatePicker
                    testid="debt-form-end"
                    value={form.end_date}
                    onChange={(v) => setForm({ ...form, end_date: v })}
                  />
                </div>
              )}
              <div className={REVOLVING_KINDS.has(form.kind) ? "col-span-2" : ""}>
                <Label>{tx(lang, "Currency", "Moneda", "Moneda")}</Label>
                <Select
                  value={form.currency || currency}
                  onValueChange={(v) => setForm({ ...form, currency: v === currency ? "" : v })}
                >
                  <SelectTrigger data-testid="debt-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(fxList.length ? fxList : [currency]).map((c) => (
                      <SelectItem key={c} value={c}>{c}{c === currency ? ` · ${tx(lang, "primary", "principal", "principal")}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Cuenta de origen — cuenta de donde sale la cuota auto-generada
                  en /Pagos cuando el usuario marca pagado. Si está vacío, el
                  backend cae a la primary account (legacy). Crítico para
                  freelancers que pagan la TC USD con una USD wallet vs. ARS card. */}
              <div className="col-span-2">
                <AccountPickerField
                  label={tx(lang, "Account that funds the installment", "Cuenta para pagar la cuota", "Cuenta para pagar la cuota")}
                  name="debt-form-account-id"
                  accounts={cashAccounts}
                  value={form.account_id}
                  onChange={(v) => setForm({ ...form, account_id: v })}
                  primaryOption={{ label: tx(lang, "Primary account (default)", "Cuenta principal (default)", "Cuenta principal (default)") }}
                  hint={tx(lang, "Auto-converted when the account's currency differs from the debt's.", "El sistema convierte automáticamente si la moneda de la cuenta difiere de la deuda.", "El sistema convierte automáticamente si la moneda de la cuenta difiere de la deuda.")}
                  isEs={lang === "es"}
                  testid="debt-form-account-id-field"
                />
              </div>
              <div className="col-span-2">
                <Label>{tx(lang, "Notes", "Notas", "Notas")}</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
          </div>
      </BottomSheet>

      <BottomSheet
        open={!!payTarget}
        onOpenChange={(o) => !o && setPayTarget(null)}
        testid="debt-pay-dialog"
        size="md"
        title={tx(lang, `Pay ${payTarget?.name}`, `Pago a ${payTarget?.name}`, `Pago a ${payTarget?.name}`)}
        description={tx(lang, "Log a payment toward this debt", "Registrar un pago a esta deuda", "Registrar un pago a esta deuda")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setPayTarget(null)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="debt-pay-confirm" onClick={submitPay} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{tx(lang, "Record payment", "Registrar pago", "Registrar pago")}</Button>
          </div>
        }
      >
        <Label>{tx(lang, "Amount", "Monto", "Monto")}</Label>
        <Input data-testid="debt-pay-amount" type="number" inputMode="decimal" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="h-12 text-base rounded-xl mt-1.5" />
      </BottomSheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.name}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="debt-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
